import { CACHE_NAME, SDS_CACHE_KEY } from 'api';
import { format, isToday, parseISO } from 'date-fns';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import i18n from 'i18next';
import { ExportUserSDSDto, LoadSdsCacheDto, SDSInterface } from 'interfaces';

export function validateToken(token: string | null): boolean {
  if (!token) {
    return false;
  }
  const pattern = /^([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_\-+/=]*)/g;
  return pattern.test(token);
}

export const putCache = (url: string, data: any) => {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'PUT_CACHE',
      url: url,
      data: data,
    });
  } else {
    console.error('Service Worker controller not available.');
  }
};

export const getCachedChunkSdsData = async () => {
  const cache = await caches.open(CACHE_NAME);
  const requests = await cache.keys();
  const matched = [];

  const pattern = new RegExp(`^/${SDS_CACHE_KEY}(-chunk-\\d+)?$`);
  for (const req of requests) {
    const reqUrl = new URL(req.url);
    if (pattern.test(reqUrl.pathname)) matched.push(reqUrl.pathname);
  }
  matched.sort((a, b) => {
    const splittedA = Number(a.split('-').at(-1));
    const splittedB = Number(b.split('-').at(-1));
    if (isNaN(splittedA)) return -1;
    if (isNaN(splittedB)) return 1;
    return splittedA - splittedB;
  });

  let cacheData = {};
  for (let chunkKey of matched) {
    const cachedResponse = await cache.match(chunkKey);
    if (cachedResponse) {
      const chunkData = await cachedResponse.json();
      cacheData = { ...cacheData, ...chunkData };
    }
  }
  return cacheData;
};

export const loadSdsCache = async (
  searchText: string,
  offset: number,
  pageSize: number,
): Promise<LoadSdsCacheDto> => {
  const data: ExportUserSDSDto = await getCachedChunkSdsData();

  const s = searchText ? searchText.toLowerCase() : '';
  const splitted = s.split(/\s|,|;|:|-|\(|\)|'|"|\.|<|>|\||-|_/g);
  let index = 0;
  const tableData = [];
  for (const sdsId in data) {
    const sds: SDSInterface = data[Number(sdsId)];

    if (!s) {
      if (index >= offset * pageSize && index < (offset + 1) * pageSize)
        tableData.push(sds);

      index++;
      continue;
    }

    let matched = true;
    for (const index in splitted) {
      const value = splitted[index];
      if (!value) continue;
      if (
        !(
          (sds.product_name &&
            sds.product_name.toLowerCase().includes(value)) ||
          (sds.supplier_name &&
            sds.supplier_name.toLowerCase().includes(value)) ||
          (sds.product_code &&
            sds.product_code.toLowerCase().includes(value)) ||
          (sds.ean_code && sds.ean_code.toLowerCase() === value) ||
          (sds.upc_code && sds.upc_code.toLowerCase() === value)
        )
      ) {
        matched = false;
        break;
      }
    }

    if (matched) {
      if (index >= offset * pageSize && index < (offset + 1) * pageSize)
        tableData.push(sds);

      index++;
    }
  }
  return {
    data: tableData,
    count: index,
  };
};

export const cacheFile = async (
  url: string,
  file: Blob,
  contentType: string,
) => {
  if ('caches' in window) {
    const cache = await caches.open(CACHE_NAME);
    const response = new Response(file, {
      headers: { 'Content-Type': contentType },
    });
    await cache.put(url, response);
  }
};

export const isMobile = (): boolean => {
  return window.matchMedia('(max-width: 767px)').matches;
};

export const isNumber = (value: string) => {
  const numStrPattern =
    /^-?(\d+\.?\d*)$|(\d*\.?\d+)$|^-?(\d+,?\d*)$(\d*,?\d+)$/;
  return numStrPattern.test(value);
};

export const getRecentYears = (count: number): number[] => {
  let currYear = new Date().getFullYear();
  return [...Array(count)].map((_, i) => currYear - i);
};

export const trimString = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  return value.trim();
};

export const formatISODate = (date: string): string | null => {
  if (!date) return null;
  try {
    const convertedDate = parseISO(date);
    let formattedDate = format(convertedDate, 'dd/MM/yyyy');
    if (isToday(convertedDate))
      formattedDate = format(convertedDate, 'dd/MM/yyyy HH:mm:ss');
    return formattedDate;
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const renderSnackbar = (message: string): void => {
  enqueueSnackbar(message, {
    action: key => (
      <p className="snackbar-action-btn" onClick={() => closeSnackbar(key)}>
        {i18n.t('common:close')}
      </p>
    ),
  });
};

export const removeCache = async (): Promise<boolean> => {
  return new Promise(resolve => {
    caches.keys().then(cacheNames => {
      try {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
        resolve(true);
      } catch (err) {
        console.error(err);
        resolve(false);
      }
    });
  });
};

export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export const isInStandaloneMode = (): boolean => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator && (window.navigator as any).standalone)
  );
};
